import {
  Button,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { rolesApi, type AppRole } from '@/api/users';
import { PageHeader } from '@/shared/components/page-header';

const QK = ['users', 'roles'] as const;

export function RolesPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: QK, queryFn: rolesApi.list });

  const columns: TableProps<AppRole>['columns'] = [
    {
      title: 'Role',
      dataIndex: 'name',
      render: (n: string, row) => (
        <Space>
          <Typography.Text strong>{n}</Typography.Text>
          {row.system && <Tag color="processing">System</Tag>}
          {!row.active && <Tag>Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (d: string | null) =>
        d ? (
          <Typography.Text type="secondary">{d}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: '',
      width: 130,
      align: 'right',
      render: (_, row) => (
        <Button
          type="text"
          size="small"
          icon={<RightOutlined />}
          iconPosition="end"
          onClick={() => navigate(`/users/roles/${row.id}`)}
        >
          Permissions
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module"
        title="User management"
        description="Roles ship with the system. Permissions per role are editable; the role list itself isn't."
        count={data ? { value: data.length, label: 'roles' } : undefined}
      />

      <Segmented
        options={[
          { label: 'Users', value: 'users' },
          { label: 'Roles & permissions', value: 'roles' },
        ]}
        value="roles"
        onChange={(val) => {
          if (val === 'users') navigate('/users');
        }}
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data ?? []}
        pagination={false}
        onRow={(row) => ({
          onClick: () => navigate(`/users/roles/${row.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
